import type { Route } from 'next';
import { usePathname, useRouter } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useLocale } from 'next-intl';
import { useTrackingEvents } from '@/hooks/use-tracking-events';
import { authClient } from '@/lib/auth-client';
import { trpc } from '@/lib/trpc';
import { triggerHaptic } from '@/lib/utils/feedback';

// Haptic feedback constants
const HAPTIC_INTENSITY_LIGHT = 25;
const HAPTIC_INTENSITY_MEDIUM = 30;

type CheckoutOptions = {
  couponId?: string;
};

export function useSubscriptionManager() {
  const router = useRouter();
  const pathname = usePathname();
  const locale = useLocale();
  const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);
  const { data: session } = authClient.useSession();
  const hasSession = Boolean(session?.user);

  // Mutations
  const portalSession = trpc.user.billing.createCustomerPortalSession.useMutation({
    onSuccess: ({ url }) => window.location.assign(url),
    onError: (error) => toast.error(error.message || 'Could not open customer portal.'),
  });
  const cancelSubscription = trpc.user.billing.cancelSubscription.useMutation();
  const reactivateSubscription = trpc.user.billing.reactivateSubscription.useMutation();
  const updateSubscription = trpc.user.billing.updateSubscription.useMutation();
  const getSubscriptionDetails = trpc.user.billing.getSubscriptionDetails.useQuery(undefined, {
    enabled: hasSession,
  });
  const tracking = useTrackingEvents();

  const isOverrideSubscription =
    getSubscriptionDetails.data && 'id' in getSubscriptionDetails.data
      ? getSubscriptionDetails.data.id === 'override'
      : false;

  // Create checkout session
  const handleCreateCheckoutSession = useCallback(
    (planId: string, billingCycle: 'monthly' | 'annual', options?: CheckoutOptions) => {
      if (isOverrideSubscription) {
        toast.error('Checkout indisponível enquanto estiver simulando um plano.');
        return;
      }
      triggerHaptic(HAPTIC_INTENSITY_MEDIUM);

      tracking.trackCheckoutRedirect({ plan: planId, billingCycle });
      if (typeof window === 'undefined') {
        return;
      }

      const params = new URLSearchParams({
        plan: planId,
        billing: billingCycle,
      });
      if (options?.couponId) {
        params.set('coupon', options.couponId);
      }

      setIsCreatingCheckout(true);
      const localePrefix = pathname?.match(/^\/(en|pt|es)(?=\/|$)/)?.[0] ?? '';
      router.push(`${localePrefix}/checkout?${params.toString()}` as Route);
    },
    [isOverrideSubscription, pathname, router, tracking.trackCheckoutRedirect]
  );

  // Open in-app subscription settings
  const handleOpenCustomerPortal = useCallback(() => {
    if (isOverrideSubscription) {
      toast.error('Portal indisponível enquanto estiver simulando um plano.');
      return;
    }
    triggerHaptic(HAPTIC_INTENSITY_LIGHT);
    portalSession.mutate({ locale: locale === 'pt' || locale === 'es' ? locale : 'en' });
  }, [isOverrideSubscription, locale, portalSession]);

  // Cancel subscription
  const handleCancelSubscription = useCallback(
    (cancelAtPeriodEnd = true, reason?: string) => {
      if (isOverrideSubscription) {
        toast.error('Ação indisponível em sessão simulada.');
        return;
      }
      triggerHaptic(HAPTIC_INTENSITY_MEDIUM);

      cancelSubscription.mutate(
        { cancelAtPeriodEnd, reason },
        {
          onSuccess: () => {
            tracking.trackSubscriptionCancelled({
              cancel_at_period_end: cancelAtPeriodEnd,
              reason: reason ?? null,
              source: 'billing_manager',
            });
            toast.success(
              cancelAtPeriodEnd
                ? 'Subscription will be cancelled at the end of the billing period'
                : 'Subscription cancelled immediately'
            );
          },
          onError: (error) => {
            if (error.data?.code === 'FORBIDDEN') {
              handleOpenCustomerPortal();
              return;
            }
            toast.error(error.message || 'Could not cancel subscription.');
          },
        }
      );
    },
    [cancelSubscription, handleOpenCustomerPortal, isOverrideSubscription, tracking]
  );

  // Reactivate subscription
  const handleReactivateSubscription = useCallback(() => {
    if (isOverrideSubscription) {
      toast.error('Ação indisponível em sessão simulada.');
      return;
    }
    triggerHaptic(HAPTIC_INTENSITY_MEDIUM);

    reactivateSubscription.mutate(undefined, {
      onSuccess: () => {
        toast.success('Subscription reactivated successfully');
      },
      onError: (error) => {
        if (error.data?.code === 'FORBIDDEN') {
          handleOpenCustomerPortal();
          return;
        }
        toast.error(error.message || 'Could not reactivate subscription.');
      },
    });
  }, [reactivateSubscription, handleOpenCustomerPortal, isOverrideSubscription]);

  const handleUpdateSubscription = useCallback(
    (planId: 'cosmic-navigator' | 'cosmic-oracle', billingCycle: 'monthly' | 'annual') => {
      if (isOverrideSubscription) {
        toast.error('Ação indisponível em sessão simulada.');
        return;
      }
      triggerHaptic(HAPTIC_INTENSITY_MEDIUM);

      updateSubscription.mutate(
        { planId, billingCycle },
        {
          onSuccess: () => {
            toast.success('Subscription updated successfully');
          },
          onError: (error) => {
            toast.error(error.message || 'Could not update subscription.');
          },
        }
      );
    },
    [isOverrideSubscription, updateSubscription]
  );

  useEffect(() => {
    if (!pathname) {
      return;
    }
    setIsCreatingCheckout(false);
  }, [pathname]);

  // Check if subscription is scheduled for cancellation
  const isScheduledForCancellation = Boolean(
    getSubscriptionDetails.data && 'cancelAtPeriodEnd' in getSubscriptionDetails.data
      ? getSubscriptionDetails.data.cancelAtPeriodEnd
      : false
  );

  // Get subscription status
  const subscriptionStatus =
    getSubscriptionDetails.data && 'status' in getSubscriptionDetails.data
      ? getSubscriptionDetails.data.status
      : undefined;
  const hasSubscription =
    getSubscriptionDetails.data && 'hasSubscription' in getSubscriptionDetails.data
      ? getSubscriptionDetails.data.hasSubscription
      : false;

  return {
    // Data
    subscriptionDetails: getSubscriptionDetails.data,
    isOverrideSubscription,
    isScheduledForCancellation,
    subscriptionStatus,
    hasSubscription,

    // Loading states
    isLoading: getSubscriptionDetails.isLoading,
    isCreatingCheckout,
    isOpeningPortal: portalSession.isPending,
    isCancelling: cancelSubscription.isPending,
    isReactivating: reactivateSubscription.isPending,
    isUpdatingSubscription: updateSubscription.isPending,

    // Actions
    createCheckoutSession: handleCreateCheckoutSession,
    openCustomerPortal: handleOpenCustomerPortal,
    cancelSubscription: handleCancelSubscription,
    reactivateSubscription: handleReactivateSubscription,
    updateSubscription: handleUpdateSubscription,

    // Refetch
    refetch: getSubscriptionDetails.refetch,
  };
}
